import {
  get,
  flow,
  filter,
  forEach,
  isArray,
  keys,
  reduce,
  toNumber,
  toString,
  isEmpty,
  isNumber,
  isNaN,
  isBoolean,
} from 'lodash/fp';

const zipCodeRegexByLocale = {
  'de': /\b\d{5}$/,
  'nl': /^[1-9]\d{3} ?(?!sa|sd|ss|SA|SD|SS)[a-zA-z]{2}$/,
  'es': /\b[0-9]{5}$/,
  'it': /^\d{5}$/,
  'fr': /^\d{5}$/,
  'sv-se': /^\d{3}\s*\d{2}$/,
  'de-at': /^\d{4}$/,
  'nl-be': /^\d{4}$/,
};

const emailRegex =
  /^([a-zA-Z0-9_\.\-\+])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/;

const getError = (validators = [], value, values) => {
  let error;

  forEach((validator) => {
    error = validator(value, values);

    if (error) {
      return false;
    }
  }, validators);

  return error;
};

const validate = (rules = {}, values = {}) =>
  flow(
    keys,
    reduce((errors, key) => {
      const rule = rules[key];

      if (isArray(rule)) {
        return {
          ...errors,
          [key]: getError(rule, values[key], values),
        };
      }

      return {
        ...errors,
        [key]: validate(rule, values[key]),
      };
    }, {}),
  )(rules);

export const required = (value) => {
  return value ? null : __('validation.required');
};

export const requiredIn =
  (...args) =>
  (value) =>
    flow(
      filter((arg) => !get([arg], value)),
      (invalidItems) =>
        invalidItems && invalidItems.length > 0
          ? __('validation.required')
          : null,
    )(args);

export const notEmpty = (value) => {
  if ((isNumber(value) && !isNaN(value)) || isBoolean(value)) {
    return null;
  }

  if (isEmpty(value)) {
    return __('validation.required');
  }

  return null;
};

export const hasPlaceId = (value) => {
  return value && value.placeId ? null : __('validation.required');
};

export const wholeNumberOnly = (value) => {
  return /^\d*$/g.test(toString(value).trim())
    ? null
    : __('validation.wholeNumberOnly');
};

export const smallerThen = (maxValue) => (value) => {
  return toNumber(value) < maxValue ? null : __('validation.numberIsTooBig');
};

export const monthAfterDate =
  (yearFieldName, yearsInPrior, errorMessage) => (value, form) => {
    const monthValue = toNumber(value);
    const yearValue = toNumber(get(yearFieldName, form));

    if (!monthValue || !yearValue) {
      return null;
    }
    const ethalonDate = new Date();
    const selectedDate = new Date();

    ethalonDate.setDate(1);
    ethalonDate.setFullYear(ethalonDate.getFullYear() - yearsInPrior);
    selectedDate.setDate(1);
    selectedDate.setMonth(monthValue - 1);
    selectedDate.setFullYear(yearValue);
    if (selectedDate < ethalonDate) {
      return errorMessage;
    }

    return null;
  };

export const notEmptyIfOtherFieldIsNotEmpty = (fieldName) => (value, form) => {
  const pairFieldValue = get(fieldName, form);
  const isPairFieldNotEmpty = notEmpty(pairFieldValue);

  if (!isPairFieldNotEmpty) {
    return notEmpty(value);
  }

  return null;
};

export const zipcode = (locale) => (value) => {
  const localeRegex = get([locale], zipCodeRegexByLocale);

  if (!localeRegex) {
    return null;
  }

  return !localeRegex.test(value) ? __('validation.invalidZipCode') : null;
};

export const email = (value) => {
  if (!value || value === null || value === undefined) {
    return null;
  }

  return !emailRegex.test(value) ? __('validation.invalidEmail') : null;
};

export const createValidator = (rules) => (values) => validate(rules, values);
